import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Line, Text, Billboard } from "@react-three/drei";
import * as THREE from "three";

// Tracker colors (per PRD): Brian = Blue, Stanley = Green. Stairwell A = TAB gold.
const BRIAN = "#62a8e5";
const STANLEY = "#4cd07a";
const GOLD = "#f2c85b";
const SEVERED = "#7a1f1f";

const FLOORS = 84;
const H = 4.2; // tower model height in world units
const HALF = H / 2;
const yFor = (f) => (Math.max(0, Math.min(FLOORS, f)) / FLOORS) * H - HALF;

// Stairwell A — a switch-backing gold conduit tracing the descent down the core.
const STAIR_PTS = (() => {
  const pts = [];
  for (let i = 0; i <= 20; i++) {
    const f = FLOORS - (i / 20) * FLOORS;
    const x = (i % 2 === 0 ? 0.32 : 0.18) * (i % 2 === 0 ? 1 : -1) + 0.06;
    pts.push([x, yFor(f), 0.16]);
  }
  return pts;
})();

function TowerCore() {
  const boxGeo = useMemo(() => new THREE.BoxGeometry(1.15, H, 1.15), []);
  const edges = useMemo(() => new THREE.EdgesGeometry(boxGeo), [boxGeo]);
  const floorLines = useMemo(() => {
    const g = [];
    for (let f = 6; f < FLOORS; f += 6) g.push(yFor(f));
    return g;
  }, []);
  return (
    <group>
      {/* translucent tower volume */}
      <mesh geometry={boxGeo}>
        <meshBasicMaterial color={0x2a2f36} transparent opacity={0.1} depthWrite={false} />
      </mesh>
      {/* wireframe shell */}
      <lineSegments geometry={edges}>
        <lineBasicMaterial color={0x8a8f99} transparent opacity={0.7} />
      </lineSegments>
      {/* floor slabs */}
      {floorLines.map((y, i) => (
        <lineSegments key={i} geometry={edges} position={[0, 0, 0]} visible={false} />
      ))}
      {floorLines.map((y, i) => (
        <mesh key={`f${i}`} position={[0, y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[1.13, 1.13]} />
          <meshBasicMaterial color={0x4a515b} transparent opacity={0.09} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      ))}
      {/* impact severance band (~78-82) */}
      <mesh position={[0, yFor(80), 0]}>
        <boxGeometry args={[1.2, yFor(82) - yFor(78), 1.2]} />
        <meshBasicMaterial color={0xb93826} transparent opacity={0.16} depthWrite={false} />
      </mesh>
      {/* impact gash — jagged red band cut across the east face */}
      <Line points={[[-0.6, yFor(83), 0.6], [0.2, yFor(80), 0.6], [0.6, yFor(78), 0.6]]} color={0xff4326} lineWidth={3} />
      {/* severed stairwells B & C */}
      <Line points={[[-0.38, HALF, -0.16], [-0.38, yFor(80), -0.16]]} color={SEVERED} lineWidth={2} />
      <Line points={[[0.42, HALF, -0.16], [0.42, yFor(80), -0.16]]} color={SEVERED} lineWidth={2} />
      {/* 'B' / 'C' labels next to the severed lines — matching the floor-label style */}
      <Billboard position={[-0.52, yFor(58), -0.16]}>
        <Text fontSize={0.15} color="#e0736a" anchorX="right" anchorY="middle" outlineWidth={0.012} outlineColor="#0a0b0d">B</Text>
      </Billboard>
      <Billboard position={[0.56, yFor(58), -0.16]}>
        <Text fontSize={0.15} color="#e0736a" anchorX="left" anchorY="middle" outlineWidth={0.012} outlineColor="#0a0b0d">C</Text>
      </Billboard>
      {/* Stairwell A — glowing gold path of salvation */}
      <Line points={STAIR_PTS} color={GOLD} lineWidth={4} />
      <Line points={STAIR_PTS} color={GOLD} lineWidth={9} transparent opacity={0.22} />

      {/* floor labels — always face the viewer */}
      {[
        { y: yFor(81), t: "81st · Rescue", c: "#4cd07a" },
        { y: yFor(31), t: "31st · Office", c: "#e4be65" },
        { y: yFor(3), t: "Lobby · Exit", c: "#cfd4dc" },
        { y: yFor(80) + 0.15, t: "Impact Gash", c: "#ff6a4e" },
      ].map((l, i) => (
        <Billboard key={i} position={[0.85, l.y, 0]}>
          <Text fontSize={0.16} color={l.c} anchorX="left" anchorY="middle" outlineWidth={0.012} outlineColor="#0a0b0d">
            {l.t}
          </Text>
        </Billboard>
      ))}
    </group>
  );
}

function Marker({ color, y, x }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const s = 1 + Math.sin(state.clock.elapsedTime * 4) * 0.18;
    ref.current.scale.setScalar(s);
  });
  return (
    <mesh ref={ref} position={[x, y, 0.18]}>
      <sphereGeometry args={[0.09, 14, 14]} />
      <meshBasicMaterial color={color} toneMapped={false} />
    </mesh>
  );
}

function Scene({ descentFloor, reunited, scrubFloor }) {
  const dy = yFor(descentFloor);
  return (
    <group>
      <TowerCore />
      {reunited ? (
        <group>
          <Marker color={BRIAN} y={dy + 0.05} x={0.06} />
          <Marker color={STANLEY} y={dy - 0.05} x={0.06} />
        </group>
      ) : (
        <group>
          <Marker color={BRIAN} y={yFor(84)} x={0.34} />
          <Marker color={STANLEY} y={yFor(81)} x={0.34} />
        </group>
      )}
      {scrubFloor != null && (
        <mesh position={[-0.34, yFor(scrubFloor), 0.18]}>
          <torusGeometry args={[0.11, 0.02, 8, 20]} />
          <meshBasicMaterial color={STANLEY} transparent opacity={0.7} toneMapped={false} />
        </mesh>
      )}
    </group>
  );
}

export default function MinimapCutaway({ descentFloor, reunited, scrubFloor }) {
  return (
    <div
      data-testid="hud-tower-minimap"
      className="hud-panel"
      style={{ padding: "10px 10px 8px", borderRadius: 6, width: 210 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "#c9a24b" }}>South Tower · Stairwell A</span>
        <span className="font-mono-tel" style={{ fontSize: 8.5, color: "#7e8594" }}>Drag to orbit</span>
      </div>
      <div
        data-testid="minimap-3d-canvas"
        style={{ position: "relative", width: 190, height: 270, overflow: "hidden", borderRadius: 4, border: "1px solid rgba(201,162,75,0.25)", cursor: "grab", touchAction: "none", background: "radial-gradient(circle at 50% 40%, rgba(24,27,32,0.9), rgba(10,11,13,0.98))" }}
      >
        <Canvas
          dpr={[1, 1.5]}
          gl={{ antialias: true, alpha: true }}
          camera={{ fov: 40, position: [3.2, 1.4, 3.4], near: 0.1, far: 40 }}
        >
          <ambientLight intensity={0.9} />
          <pointLight position={[3, 4, 3]} intensity={0.6} />
          <Scene descentFloor={descentFloor} reunited={reunited} scrubFloor={scrubFloor} />
          <OrbitControls
            enablePan={false}
            enableDamping
            dampingFactor={0.08}
            minDistance={2.6}
            maxDistance={7}
            autoRotate
            autoRotateSpeed={0.9}
            target={[0, 0, 0]}
          />
        </Canvas>
      </div>

      {/* Gold Map Legend */}
      <div data-testid="minimap-legend" style={{ marginTop: 7, borderTop: "1px solid rgba(201,162,75,0.28)", paddingTop: 6 }}>
        <div style={{ fontSize: 8.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "#c9a24b", marginBottom: 5, textAlign: "center" }}>Map Legend</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 8.5, marginBottom: 4 }} className="font-mono-tel">
          <span style={{ width: 14, height: 3, borderRadius: 2, background: GOLD, boxShadow: `0 0 6px ${GOLD}`, flexShrink: 0 }} />
          <span style={{ color: GOLD }}>Stairwell A · The Path of Salvation</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 8.5, marginBottom: 5 }} className="font-mono-tel">
          <span style={{ width: 14, height: 3, borderRadius: 2, background: SEVERED, flexShrink: 0 }} />
          <span style={{ color: "#c67a6f" }}>Stairwells B & C · Severed by impact</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8.5 }} className="font-mono-tel">
          <span style={{ color: BRIAN, display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: BRIAN, display: "inline-block" }} /> Brian · 84th</span>
          <span style={{ color: STANLEY, display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: STANLEY, display: "inline-block" }} /> Stanley · 81st</span>
        </div>
        <div style={{ fontSize: 9, color: "#7e8594", textAlign: "center", marginTop: 5 }} className="font-mono-tel">
          {reunited ? `UNITED · FLOOR ${Math.max(1, Math.round(descentFloor))}` : "AWAITING RESCUE"}
        </div>
      </div>
    </div>
  );
}
